import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box, Typography, Divider} from 'views/components';
import {Translate} from 'views/module';

import SlipeTimeline from './SlipTimeline';
import SlipExpensesDetails from './SlipExpenseDetails';
import SlipPaymentDetails from './SlipPaymentDetails';

import WorkHourCalculator from 'utils/work_hour/work_hour_calculator';
import moment from 'moment-timezone';

const SlipHourDetails = (props) => {
  const slipDetail = props.slipDetail;

  const hours = slipDetail.hours;
  const hourlyRate = Number(slipDetail.clientPaymentPlan?.hourlyRate) || null;

  const createShiftPct = (shift) => {
    const times = [shift.scheduledStart, shift.scheduledEnd, shift.actualIn, shift.actualOut].filter(Boolean);
    const earliest = moment.min(times);
    const latest = moment.max(times);
    const minTime = earliest.clone().subtract(2, 'hours');
    const maxTime = latest.clone().add(2, 'hours');
    const totalMs = Math.max(maxTime.diff(minTime), 1);

    return {
      percent: (t) => (t.diff(minTime) / totalMs) * 100,
      minTime,
      maxTime,
    };
  };

  const hoursSorted = hours.sort((a, b) => {
    return moment(a.eventData.startTime).diff(moment(b.eventData.startTime));
  });

  const clockedInShiftData = React.useMemo(() => (
    hoursSorted.map((hour) => {
      const tz = hour.eventData.timezone;
      const scheduledStart = hour.eventData.startTime;
      const scheduledEnd = hour.eventData.endTime;

      const clockInUpdate = hour.event?.eventUpdateDatas?.find((update) => update.type === 'clockInConfirmation');
      const clockOutUpdate = hour.event?.eventUpdateDatas?.find((update) => update.type === 'clockOutConfirmation');

      if (!clockInUpdate || !clockOutUpdate) {
        return {
          scheduledStart: moment.tz(scheduledStart, tz),
          scheduledEnd: moment.tz(scheduledEnd, tz),
          actualIn: null,
          actualOut: null,
          roundedIn: null,
          roundedIn: null,
        };
      }

      const actualIn = clockInUpdate.data.suggestedClockinTimeFormatted;
      const actualOut = clockOutUpdate.data.suggestedClockoutTimeFormatted;

      const {roundedIn, roundedOut} = WorkHourCalculator.calculateWorkedHours({
        scheduleStartIso: scheduledStart,
        scheduleEndIso: scheduledEnd,
        actualInIso: actualIn,
        actualOutIso: actualOut,
      });

      return {
        scheduledStart: moment.tz(scheduledStart, tz),
        scheduledEnd: moment.tz(scheduledEnd, tz),
        actualIn: moment.tz(actualIn, tz),
        actualOut: moment.tz(actualOut, tz),
        roundedIn: moment.tz(roundedIn, tz),
        roundedOut: moment.tz(roundedOut, tz),
      };
    })
  ), [hoursSorted]);

  return (
    <Stack
      sx={{
        p: 2,
      }}
      spacing={1}
    >
      <Typography
        sx={{
          fontWeight: 'bold',
        }}
      >
        <Translate expression='caregiver.CaregiverDetail.Payment.WorkSummary.HoursWorked.Title' />
      </Typography>
      <Box pt={2} ml={{xs: 1, md: 5}}>
        {
          hoursSorted.map((shift, index) => {
            const timezone = shift.eventData.timezone;
            const date = moment.tz(shift.eventData.startTime, timezone).format('dddd, MMM D');
            const title = shift.eventInfo?.title;

            const currentShift = clockedInShiftData[index];
            const shiftPct = createShiftPct(currentShift);

            const expenses = shift.expenses || [];
            const endTime = shift.eventData.endTime;

            return (
              <Box
                key={index}
                sx={{
                  'pageBreakInside': 'avoid',
                  'breakInside': 'avoid',
                  'orphans': 2,
                  'widows': 2,
                  '@media print': {
                    pageBreakInside: 'avoid',
                    breakInside: 'avoid',
                    marginBottom: '8px',
                  },
                }}
              >
                <SlipeTimeline
                  index={index}
                  date={date}
                  title={title}
                  shiftPct={shiftPct}
                  currentShift={currentShift}
                />
                {
                  hourlyRate && currentShift.actualIn &&
                  <SlipPaymentDetails hourlyRate={hourlyRate} currentShift={currentShift} endTime={endTime} />
                }
                {
                  expenses.length > 0 &&
                  <SlipExpensesDetails expenses={expenses} />
                }
                <Divider sx={{pt: 1, mb: 1}} />
              </Box>
            );
          })
        }
      </Box>
    </Stack>
  );
};

SlipHourDetails.propTypes = {
  slipDetail: PropTypes.object.isRequired,
};

export default React.memo(SlipHourDetails);
